import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ResearchSandbox = ({ selectedDataset, onQueryExecute }) => {
  const [queryBuilder, setQueryBuilder] = useState({
    timeRange: 'last_30_days',
    countries: [],
    narrativeThemes: [],
    confidenceThreshold: 0.7,
    includeMetadata: true,
    anonymizationLevel: 'high'
  });
  const [customQuery, setCustomQuery] = useState('');
  const [queryMode, setQueryMode] = useState('builder');
  const [isExecuting, setIsExecuting] = useState(false);

  const timeRangeOptions = [
    { value: 'last_7_days', label: 'Last 7 days' },
    { value: 'last_30_days', label: 'Last 30 days' },
    { value: 'last_90_days', label: 'Last 90 days' },
    { value: 'last_6_months', label: 'Last 6 months' },
    { value: 'last_year', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const countryOptions = [
    { value: 'DE', label: 'Germany' },
    { value: 'FR', label: 'France' },
    { value: 'IT', label: 'Italy' },
    { value: 'ES', label: 'Spain' },
    { value: 'PL', label: 'Poland' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'BE', label: 'Belgium' },
    { value: 'AT', label: 'Austria' },
    { value: 'CZ', label: 'Czech Republic' },
    { value: 'HU', label: 'Hungary' },
    { value: 'RO', label: 'Romania' },
    { value: 'BG', label: 'Bulgaria' },
    { value: 'HR', label: 'Croatia' },
    { value: 'SK', label: 'Slovakia' },
    { value: 'SI', label: 'Slovenia' },
    { value: 'LT', label: 'Lithuania' },
    { value: 'LV', label: 'Latvia' },
    { value: 'EE', label: 'Estonia' },
    { value: 'FI', label: 'Finland' },
    { value: 'SE', label: 'Sweden' },
    { value: 'DK', label: 'Denmark' },
    { value: 'IE', label: 'Ireland' },
    { value: 'PT', label: 'Portugal' },
    { value: 'GR', label: 'Greece' },
    { value: 'CY', label: 'Cyprus' },
    { value: 'MT', label: 'Malta' },
    { value: 'LU', label: 'Luxembourg' }
  ];

  const narrativeThemeOptions = [
    { value: 'health_misinformation', label: 'Health Misinformation' },
    { value: 'election_integrity', label: 'Election Integrity' },
    { value: 'climate_denial', label: 'Climate Denial' },
    { value: 'economic_manipulation', label: 'Economic Manipulation' },
    { value: 'migration_narratives', label: 'Migration Narratives' },
    { value: 'eu_skepticism', label: 'EU Skepticism' },
    { value: 'foreign_interference', label: 'Foreign Interference' },
    { value: 'conspiracy_theories', label: 'Conspiracy Theories' },
    { value: 'social_division', label: 'Social Division' },
    { value: 'technology_fears', label: 'Technology Fears' }
  ];

  const anonymizationOptions = [
    { value: 'minimal', label: 'Minimal (Institutional Access)' },
    { value: 'standard', label: 'Standard (Academic Research)' },
    { value: 'high', label: 'High (Public Research)' },
    { value: 'maximum', label: 'Maximum (Open Access)' }
  ];

  const queryModeOptions = [
    { value: 'builder', label: 'Query Builder' },
    { value: 'sql', label: 'SQL Query' },
    { value: 'api', label: 'API Explorer' }
  ];

  const sampleQueries = [
    {
      name: 'Cross-Border Coordination Analysis',
      description: 'Identify narratives that appear across multiple EU countries within 48 hours',
      query: `SELECT narrative_id, countries, first_appearance, coordination_score
FROM narratives 
WHERE coordination_score > 0.8 
AND country_count >= 3 
AND time_window <= 48
ORDER BY coordination_score DESC
LIMIT 100;`
    },
    {
      name: 'Temporal Trend Analysis',
      description: 'Analyze narrative evolution patterns over time periods',
      query: `SELECT DATE_TRUNC('week', timestamp) as week,
       narrative_theme,
       COUNT(*) as mentions,
       AVG(virality_score) as avg_virality
FROM narrative_events 
WHERE timestamp >= NOW() - INTERVAL '90 days'
GROUP BY week, narrative_theme
ORDER BY week DESC, avg_virality DESC;`
    },
    {
      name: 'Platform Propagation Patterns',
      description: 'Track how narratives spread across different platforms',
      query: `SELECT platform_type,
       narrative_id,
       propagation_velocity,
       reach_metrics,
       engagement_patterns
FROM platform_signals 
WHERE narrative_id IN (SELECT id FROM high_impact_narratives)
ORDER BY propagation_velocity DESC;`
    }
  ];

  const handleQueryExecute = async () => {
    setIsExecuting(true);
    
    // Simulate query execution
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const mockResults = {
      totalRecords: Math.floor(Math.random() * 10000) + 1000,
      executionTime: Math.floor(Math.random() * 500) + 100,
      dataPoints: Math.floor(Math.random() * 50) + 10,
      query: queryMode === 'builder' ? 'Generated from Query Builder' : customQuery,
      timestamp: new Date()?.toISOString()
    };
    
    onQueryExecute(mockResults);
    setIsExecuting(false);
  };

  const handleSampleQuerySelect = (query) => {
    setCustomQuery(query?.query);
    setQueryMode('sql');
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Research Sandbox</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedDataset ? `Analyzing: ${selectedDataset?.title}` : 'Select a dataset to begin analysis'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-xs font-medium text-success">GDPR Compliant</span>
          </div>
        </div>

        <Select
          options={queryModeOptions}
          value={queryMode}
          onChange={setQueryMode}
          label="Query Mode"
          className="mb-4"
        />
      </div>
      <div className="p-6">
        {queryMode === 'builder' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="Time Range"
                options={timeRangeOptions}
                value={queryBuilder?.timeRange}
                onChange={(value) => setQueryBuilder(prev => ({ ...prev, timeRange: value }))}
              />
              
              <Select
                label="Countries"
                options={countryOptions}
                value={queryBuilder?.countries}
                onChange={(value) => setQueryBuilder(prev => ({ ...prev, countries: value }))}
                multiple
                searchable
                placeholder="Select countries..."
              />
            </div>

            <Select
              label="Narrative Themes"
              options={narrativeThemeOptions}
              value={queryBuilder?.narrativeThemes}
              onChange={(value) => setQueryBuilder(prev => ({ ...prev, narrativeThemes: value }))}
              multiple
              searchable
              placeholder="Select themes to analyze..."
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confidence Threshold: {queryBuilder?.confidenceThreshold}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={queryBuilder?.confidenceThreshold}
                  onChange={(e) => setQueryBuilder(prev => ({ 
                    ...prev, 
                    confidenceThreshold: parseFloat(e?.target?.value) 
                  }))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Low (0.0)</span>
                  <span>High (1.0)</span>
                </div>
              </div>

              <Select
                label="Anonymization Level"
                options={anonymizationOptions}
                value={queryBuilder?.anonymizationLevel}
                onChange={(value) => setQueryBuilder(prev => ({ ...prev, anonymizationLevel: value }))}
              />
            </div>

            <div className="space-y-3">
              <Checkbox
                label="Include metadata and provenance information"
                checked={queryBuilder?.includeMetadata}
                onChange={(e) => setQueryBuilder(prev => ({ 
                  ...prev, 
                  includeMetadata: e?.target?.checked 
                }))}
              />
            </div>
          </div>
        )}

        {queryMode === 'sql' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Custom SQL Query
              </label>
              <textarea
                value={customQuery}
                onChange={(e) => setCustomQuery(e?.target?.value)}
                placeholder="Enter your SQL query here..."
                className="w-full h-40 p-3 border border-border rounded-lg bg-background text-foreground font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Sample Queries</h3>
              <div className="space-y-2">
                {sampleQueries?.map((sample, index) => (
                  <div
                    key={index}
                    className="p-3 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-smooth"
                    onClick={() => handleSampleQuerySelect(sample)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-medium text-foreground">{sample?.name}</h4>
                      <Icon name="Copy" size={14} className="text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">{sample?.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {queryMode === 'api' && (
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <h3 className="text-sm font-medium text-foreground mb-2">API Endpoint</h3>
              <code className="text-sm font-mono text-muted-foreground">
                GET /api/v1/research/query?dataset={selectedDataset?.id || 'dataset_id'}
              </code>
            </div>

            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Request Parameters</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="font-mono text-muted-foreground">time_range</span>
                  <span className="text-foreground">string (required)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="font-mono text-muted-foreground">countries</span>
                  <span className="text-foreground">array[string] (optional)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="font-mono text-muted-foreground">themes</span>
                  <span className="text-foreground">array[string] (optional)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="font-mono text-muted-foreground">confidence_min</span>
                  <span className="text-foreground">float (0.0-1.0)</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Estimated execution: 2-5 seconds
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Database" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Max 10,000 records per query
              </span>
            </div>
          </div>
          
          <Button
            onClick={handleQueryExecute}
            loading={isExecuting}
            disabled={!selectedDataset || (queryMode === 'sql' && !customQuery?.trim())}
            iconName="Play"
            iconPosition="left"
          >
            {isExecuting ? 'Executing...' : 'Execute Query'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResearchSandbox;